import type { WorkspacePinnedUnit } from "../lib/pinned-units";
import { getPlatformService } from "../platform";
import { frontendLog } from "../utils/frontend-logger";
import { tabSessionRegistry } from "./tab-session-registry";

class PinnedUnitSessionRegistry {
    private readonly units = new Map<string, WorkspacePinnedUnit>();
    private readonly sessionByUnit = new Map<string, string>();
    private readonly unitsBySession = new Map<string, Set<string>>();

    retain(unit: WorkspacePinnedUnit, sessionId?: string): void {
        this.units.set(unit.id, unit);
        if (sessionId) this.attachSession(unit.id, sessionId);
    }

    retainMany(units: WorkspacePinnedUnit[]): void {
        for (const unit of units) this.retain(unit);
    }

    attachSession(unitId: string, sessionId: string): void {
        const prior = this.sessionByUnit.get(unitId);
        if (prior === sessionId) return;
        if (prior) {
            const priorSet = this.unitsBySession.get(prior);
            priorSet?.delete(unitId);
            if (priorSet?.size === 0) this.unitsBySession.delete(prior);
        }
        this.sessionByUnit.set(unitId, sessionId);
        const nextSet = this.unitsBySession.get(sessionId) ?? new Set<string>();
        nextSet.add(unitId);
        this.unitsBySession.set(sessionId, nextSet);
    }

    release(unitId: string): string | undefined {
        this.units.delete(unitId);
        const sessionId = this.sessionByUnit.get(unitId);
        if (!sessionId) return undefined;
        this.sessionByUnit.delete(unitId);
        const set = this.unitsBySession.get(sessionId);
        set?.delete(unitId);
        if (set?.size === 0) this.unitsBySession.delete(sessionId);
        return sessionId;
    }

    replaceAll(units: WorkspacePinnedUnit[]): void {
        this.units.clear();
        this.sessionByUnit.clear();
        this.unitsBySession.clear();
        this.retainMany(units);
    }

    getSessionId(unitId: string): string | undefined {
        return this.sessionByUnit.get(unitId);
    }

    hasSessionLease(sessionId: string): boolean {
        return (this.unitsBySession.get(sessionId)?.size ?? 0) > 0;
    }

    hasDocumentLease(documentPath: string): boolean {
        for (const unit of this.units.values()) {
            if (unit.documentPath === documentPath) return true;
        }
        return false;
    }

    retainedSessionIds(): string[] {
        return Array.from(this.unitsBySession.keys());
    }

    clear(): void {
        this.units.clear();
        this.sessionByUnit.clear();
        this.unitsBySession.clear();
    }
}

export const pinnedUnitSessionRegistry = new PinnedUnitSessionRegistry();

function sessionHasOpenTabs(sessionId: string): boolean {
    for (const link of tabSessionRegistry.entries()) {
        if (link.sessionId === sessionId) return true;
    }
    return false;
}

export async function ensurePinnedUnitSession(
    unit: WorkspacePinnedUnit,
): Promise<string> {
    const existing = pinnedUnitSessionRegistry.getSessionId(unit.id);
    if (existing) return existing;

    pinnedUnitSessionRegistry.retain(unit);
    const result = await getPlatformService().engine.sessionCreate({
        filePath: unit.documentPath,
        includeOutputs: false,
        includePresentationContent: false,
    });
    pinnedUnitSessionRegistry.attachSession(unit.id, result.sessionId);
    return result.sessionId;
}

export function releasePinnedUnitSession(unitId: string): void {
    const sessionId = pinnedUnitSessionRegistry.release(unitId);
    if (!sessionId) return;
    if (pinnedUnitSessionRegistry.hasSessionLease(sessionId)) return;
    if (sessionHasOpenTabs(sessionId)) return;
    getPlatformService().engine.sessionClose(sessionId).catch((err) => {
        frontendLog.warn("Failed to close released pinned unit session", err, {
            source: "engine",
        });
    });
}

export function clearPinnedUnitSessions(): void {
    const sessionIds = pinnedUnitSessionRegistry.retainedSessionIds();
    pinnedUnitSessionRegistry.clear();
    const engine = getPlatformService().engine;
    for (const sessionId of sessionIds) {
        if (sessionHasOpenTabs(sessionId)) continue;
        engine.sessionClose(sessionId).catch((err) => {
            frontendLog.warn("Failed to close cleared pinned unit session", err, {
                source: "engine",
            });
        });
    }
}
